import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

export interface FormShortUrlCreateResponse {
  id: string;
  shortUrl: string;
}

export type FormLinkListPagination = {
  limit: number;
  hasNext: boolean;
  nextCursor: string | null;
  totalCount: number;
  retrievedAt: string;
};

export interface FormShortUrlListItem {
  id: string;
  shortUrl: string;
  label?: string | null;
  enabled: boolean;
  createdAt: string | null;
}

export type FormShortUrlListResponse = {
  items: FormShortUrlListItem[];
  pagination: FormLinkListPagination;
};

@Injectable({
  providedIn: 'root',
})
export class FormShortUrlService {
  private api = inject(ApiService);
  private baseUrl = '';

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * List short URLs for a form (paged). Use excludeQrBacked to omit URLs tied to QR rows (NFC list).
   */
  listShortUrls(
    workspaceId: string,
    sessionId: string,
    formId: string,
    options?: { limit?: number; cursor?: string; excludeQrBacked?: boolean },
  ): Observable<FormShortUrlListResponse> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
      formId,
    };
    if (options?.limit != null) {
      params['limit'] = options.limit;
    }
    if (options?.cursor) {
      params['cursor'] = options.cursor;
    }
    if (options?.excludeQrBacked) {
      params['excludeQrBacked'] = 'true';
    }
    return from(
      this.api.get<FormShortUrlListResponse>(
        this.baseUrl,
        '/form_short_urls_api/api/form-short-urls',
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a short URL for an NFC tag. Returns the Firestore doc ID and full short URL.
   * Optional label is stored on the short-url doc for physical tag marking (e.g. sticker text).
   */
  async createShortUrl(
    workspaceId: string,
    sessionId: string,
    formId: string,
    label?: string | null,
  ): Promise<FormShortUrlCreateResponse> {
    const body = { workspaceId, sessionId, formId, ...(label?.trim() ? { label: label.trim() } : {}) };
    const obs = await this.api.post<FormShortUrlCreateResponse, typeof body>(
      this.baseUrl,
      '/form_short_urls_api/api/form-short-urls',
      body,
    );
    const result = await firstValueFrom(obs);
    if (!result?.id || !result?.shortUrl) {
      throw new Error('Invalid response from create short URL');
    }
    return result;
  }

  /**
   * Update the enabled status of a form short URL.
   */
  async updateEnabled(id: string, enabled: boolean): Promise<void> {
    const obs = await this.api.patch<unknown, { enabled: boolean }>(
      this.baseUrl,
      `/form_short_urls_api/api/form-short-urls/${id}`,
      { enabled },
    );
    await firstValueFrom(obs);
  }

  /**
   * Set or clear the physical-tag label. Pass null or empty string to remove the field.
   */
  async updateLabel(id: string, label: string | null): Promise<void> {
    const obs = await this.api.patch<unknown, { label: string }>(
      this.baseUrl,
      `/form_short_urls_api/api/form-short-urls/${id}`,
      { label: label?.trim() ?? '' },
    );
    await firstValueFrom(obs);
  }
}
